/**
 * useGamesAndProps Hook (Web)
 * Thin wrapper around shared useGamesAndProps
 */

import { API_BASE_URL } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { createHttpClient } from '@parlay-picker/shared/api/httpClient';
import {
  type ListedGame,
  type UseGamesAndPropsResult,
  useGamesAndProps as useGamesAndPropsShared,
} from '@parlay-picker/shared/hooks';
import { useMemo } from 'react';
import { logError } from '../services/errorTracking';

// Re-export types and utilities for existing consumers
export type { BettableProp, Game, UseGamesAndPropsResult } from '@parlay-picker/shared/hooks';
export { invalidatePropsCache } from '@parlay-picker/shared/hooks';

export function useGamesAndProps(options?: { league?: string }): UseGamesAndPropsResult {
  const { authFetch } = useAuth();

  const httpClient = useMemo(
    () => createHttpClient({ authFetch, baseUrl: API_BASE_URL }),
    [authFetch],
  );

  return useGamesAndPropsShared({
    httpClient,
    league: options?.league,
    onUnknownLeague: reportUnknownLeagueGame,
  });
}

/** Reports a game the games endpoint lists under no known league; the caller leaves it out. */
export function reportUnknownLeagueGame(game: ListedGame): void {
  logError(new Error('A listed game names no league'), {
    gameId: game.id,
    sportKey: game.sport_key,
  });
}
